import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { PopoverTriggerUI } from "ui/popoverTrigger";
import { TextareaUI } from "../../../ui/textareaUI";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../ui/dialog";
import {
  User,
  Globe,
  FileText,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  Users as UsersIcon,
  Eye,
  CheckCircle,
  Clock,
  Search,
  AlertCircle,
  Check,
  ChevronsUpDown,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { Separator } from "../../../ui/separator";
import { Checkbox } from "../../../ui/checkbox";
import {
  AddpaxgetDetailsByITS,
  SaveWalkInVisaAppDraft,
} from "../../../services/visaService";
import { Alert, AlertDescription } from "../../../ui/alert";
import {
  getPassportTypeName,
  mapGender,
  normalizeDate,
} from "../../../utils/commonutils";
import { FullScreenLoading } from "../../../components/ui/loader";
import axios from "axios";
import {
  CountryApi,
  getCountries,
} from "../../../services/visaFeeMasterService";
import {
  DocumentApiResponse,
  getScreenDocuments,
  fetchAgeRulesApi,
} from "../../../services/documentService";
import { useAuth } from "../../../contexts/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../../components/ui/command";
import { cn } from "../../../ui/utils";
import ConfirmDialog from "../../../ui/ConfirmDialog";
import { usePassportOCR } from "hooks/usePassPortOcr";

interface VisaApplicantCardProps {
  onClose?: () => void;
  onSave?: (applicant: Applicant) => Promise<void>;
  forceVisaRequired?: boolean;
  tourOperatorId?: number | string;
}

interface AgeRule {
  ageRuleId: number;
  categoryName: string;
  minAge: number;
  maxAge: number;
}

interface DocumentUpload {
  documentType: string;
  documentCode?: string;
  mandatory?: boolean;
  acceptedFormatsList?: string[];
  maxSize?: number;
  fileName?: string;
  file?: File;
  uploadedDate?: string;
  screenNames: string[];
  documentMasterId: string;
}

interface FieldValidation {
  field: string;
  itsValue: string;
  passportValue: string;
  isValid: boolean;
  remark?: string;
}

export interface Applicant {
  referenceId?: string | null;
  tourOperatorId: number | string;
  ejamaatId: string;
  id: string;
  itsNo: string;
  isNonItsUser: boolean;
  // loginId: string;
  applicantType: "adult" | "infant" | "senior" | "child";
  category: string;
  guardianIts: string;
  hofIts: string;
  title: string;
  firstName: string;
  fullName: string;
  middleName: string;
  lastName: string;
  dob: string;
  age: string;
  gender: string;
  birthPlace: string;
  nationality: string;
  // Passport fields
  passportNo?: string;
  passportType?: string;
  profession: string;
  passportFirstName: string;
  passportMiddleName: string;
  passportLastName: string;
  passportArabicName: string;
  passportDob?: string;
  passportGender?: string;
  passportNationality?: string;
  passportIssueDate: string;
  passportExpiryDate: string;
  passportIssuePlace?: string;
  visaRequired: boolean;
  visaNotRequiredRemark: string;
  hasFetched?: boolean;

  // Family fields
  isFamilyOpen: boolean;
  fatherName?: string;
  fatherNameArabic?: string;
  fatherItsNo?: string;
  grandfatherName?: string;
  grandfatherNameArabic?: string;
  grandfatherItsNo?: string;
  motherName?: string;
  motherNameArabic?: string;
  motherItsNo?: string;
  mothersFatherName?: string;
  mothersFatherNameArabic?: string;
  mothersFatherItsNo?: string;

  // Validation
  validationErrors?: FieldValidation[];
  showValidationErrors?: boolean;
  documents: DocumentUpload[];
  remarks: string;
  isExpanded: boolean;
  // isSelected: boolean;
  isMiqaatApplicant?: boolean;
  formErrors?: ApplicantFormErrors;
}

// Document Master Data - This would come from API in production
interface DocumentMasterItem {
  documentId: string;
  documentName: string;
  documentCode: string;
  modules: string;
  isMandatory: boolean;
  acceptedFormatsList: string[];
  maxFileSizeKB: number;
  isActive: boolean;
  status: "active" | "inactive";
  screenNames: string[];
  isRequired: boolean;
  file: File;
}

type FieldErrorMap = Record<string, string>;

// interface PassportInfo {
//   passportType?: string;
//   passportNo?: string;
//   name?: string;
//   surname?: string;
//   sex?: "Male" | "Female";
//   dob?: string;
//   expiry?: string;
//   nationality?: string;
// }

export type ApplicantFormErrors = Partial<
  Record<
    | "itsNo"
    | "title"
    | "firstName"
    | "middleName"
    | "lastName"
    | "birthPlace"
    | "dob"
    | "gender"
    | "nationality"
    | "profession"
    | "applicantType"
    | "category"
    | "passportNo"
    | "passportType"
    | "passportIssuePlace"
    | "passportIssueDate"
    | "passportExpiryDate"
    | "passportFirstName"
    | "passportMiddleName"
    | "passportLastName"
    | "passportArabicName"
    | "passportDob"
    | "passportNationality"
    | "passportGender"
    | "fatherItsNo"
    | "fatherName"
    | "fatherNameArabic"
    | "grandfatherItsNo"
    | "grandfatherName"
    | "grandfatherNameArabic"
    | "motherItsNo"
    | "motherName"
    | "motherNameArabic"
    | "mothersFatherItsNo"
    | "mothersFatherName"
    | "mothersFatherNameArabic"
    | "remarks",
    string
  >
> & {
  visaNotRequiredRemark?: string;
  documents?: string;
};

export default function VisaApplicantCard({
  onClose,
  onSave,
  forceVisaRequired = false,
  tourOperatorId,
}: VisaApplicantCardProps) {
  const { user } = useAuth();
  const [ageRules, setAgeRules] = useState<AgeRule[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentMasterItem[]>([]);
  const [openCountryField, setOpenCountryField] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<"draft" | "submit" | null>(
    null,
  );

  const createEmptyApplicant = useCallback(
    (): Applicant => ({
      id: Date.now().toString() + Math.random(),
      tourOperatorId: Number(
        tourOperatorId ?? user?.tourOperatorId ?? 0,
      ),
      ejamaatId: "",
      itsNo: "",
      isNonItsUser: false,
      // loginId: '',
      applicantType: "adult",
      category: "",
      guardianIts: "",
      hofIts: "",
      title: "",
      firstName: "",
      fullName: "",
      middleName: "",
      lastName: "",
      dob: "",
      age: "0",
      gender: "",
      birthPlace: "",
      nationality: "",
      profession: "",
      passportNo: "",
      passportType: "",
      passportFirstName: "",
      passportMiddleName: "",
      passportLastName: "",
      passportArabicName: "",
      passportDob: "",
      passportGender: "",
      passportNationality: "",
      passportIssueDate: "",
      passportExpiryDate: "",
      passportIssuePlace: "",
      documents: documentTypes?.map((type) => ({
        documentType: type.documentName,
        documentCode: type.documentCode,
        mandatory: type.isMandatory,
        acceptedFormatsList: type.acceptedFormatsList,
        maxSize: type.maxFileSizeKB,
        screenNames: type.screenNames,
        documentMasterId: type.documentId,
      })),
      remarks: "",
      isExpanded: true,
      // isSelected: false,
      isFamilyOpen: false,
      fatherName: "",
      fatherNameArabic: "",
      fatherItsNo: "",
      grandfatherName: "",
      grandfatherNameArabic: "",
      grandfatherItsNo: "",
      motherName: "",
      motherNameArabic: "",
      motherItsNo: "",
      mothersFatherName: "",
      mothersFatherNameArabic: "",
      mothersFatherItsNo: "",
      visaRequired: true,
      visaNotRequiredRemark: "",
      validationErrors: [],
      showValidationErrors: false,
      isMiqaatApplicant: false,
    }),
    [documentTypes, tourOperatorId, user?.tourOperatorId],
  );

  // Initialize applicants with Miqaat flag based on tour type
  const initializeApplicant = () => {
    const newApplicant = createEmptyApplicant();
    return newApplicant;
  };

  const [applicants, setApplicants] = useState<Applicant[]>([
    initializeApplicant(),
  ]);
  const [countries, setCountries] = useState<CountryApi[]>([]);
  const [search, setSearch] = useState("");
  const filteredCountries = countries.filter((country) => {
    const searchValue = search.trim().toLowerCase();
    return (
      country.countryName.toLowerCase().includes(searchValue) ||
      country.nationality?.toLowerCase().includes(searchValue)
    );
  });

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importType, setImportType] = useState<"family" | "individual">(
    "individual",
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isApiFetchingCompleted, setIsApiFetchingCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titles = ["Mulla", "Shaikh", "Dr", "Mr", "Mrs", "Ms"];
  const genders = ["Male", "Female"];
  // const categories = ['Jamai', 'Mumieen', 'Kaisrali', 'Kothar Mubarak'];
  // const passportTypes = ['Normal', 'Diplomatic', 'Official', 'Service'];
  // const passportTypes = ['Personal / Ordinary', 'Diplomatic', 'Service/Official'];
  const passportTypes = [
    "Personal / Ordinary",
    "Diplomatic",
    "Service / Official",
    "Emergency Passport",
    "Refugee Travel Document",
    "Stateless Passport",
    "Alien Passport",
    "Laissez-Passer",
    "Military Passport",
  ];
  const allowedScreens = useMemo(() => ["Visa Add Applicant"], []);

  const { scanPassport, ocrLoading } = usePassportOCR();

  const toggleFamilySection = (id: string) => {
    setApplicants((prev) =>
      prev.map((applicant) =>
        applicant.id === id
          ? { ...applicant, isFamilyOpen: !applicant.isFamilyOpen }
          : applicant,
      ),
    );
  };

  // Clean up localStorage when component unmounts
  useEffect(() => {
    loadCountries();
    return () => {
      localStorage.removeItem("tourType");
      localStorage.removeItem("selectedMiqaat");
    };
  }, []);

  // LOAD NATIONALITY
  const loadCountries = async () => {
    try {
      const res = await getCountries();
      setCountries(res.filter((c) => c.isActive));
    } catch {
      toast.error("Failed to load countries");
    }
  };

  const fetchDocuments = useCallback(async (screenName: string) => {
    setLoading(true);
    try {
      const result: DocumentApiResponse[] | null =
        await getScreenDocuments(screenName);

      if (!result || result.length === 0) {
        setDocumentTypes([]);
        return;
      }

      const documentOrder = (documentName: string) => {
        const normalizedName = documentName.trim().toLowerCase();
        if (normalizedName === "photo") return 0;
        if (normalizedName === "passport copy") return 1;
        return 2;
      };

      const mappedDocuments: DocumentMasterItem[] = result
        .map((doc) => ({
          documentId: String(doc.documentId),
          documentName: doc.documentName,
          documentCode: doc.documentCode ?? "",
          modules: doc.modules ?? "",
          isMandatory: doc.isMandatory,
          acceptedFormatsList: doc.acceptedFormatsList ?? [],
          maxFileSizeKB: doc.maxFileSizeKB ?? 0,
          isActive: doc.isActive ?? true,
          status: doc.isActive ? ("active" as const) : ("inactive" as const),
          screenNames: doc.screenNames ?? [],
          isRequired: doc.isMandatory,
          file: null as unknown as File,
        }))
        .sort(
          (firstDocument, secondDocument) =>
            documentOrder(firstDocument.documentName) -
            documentOrder(secondDocument.documentName),
        );

      setDocumentTypes(mappedDocuments);
      setIsApiFetchingCompleted(true);
    } catch (error) {
      console.error("Failed to load documents", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAgeRules = useCallback(async () => {
    try {
      const result = await fetchAgeRulesApi();
      if (!result || result.length === 0) {
        return;
      }
      setAgeRules(result?.items);
    } catch (error) {
      console.error("Failed to load documents", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments("visa_add_applicant");
    fetchAgeRules();
  }, [fetchDocuments, fetchAgeRules]);
  // FETCH DOCUMENTS END

  useEffect(() => {
    const newApplicant = createEmptyApplicant();
    setApplicants([newApplicant]);
  }, [isApiFetchingCompleted, createEmptyApplicant]);

  const getApplicantTypeFromAge = useCallback(
    (age: number) => {
      const matchedRule = ageRules.find(
        (rule) => age >= rule.minAge && age <= rule.maxAge,
      );

      if (!matchedRule) return "adult"; // fallback

      const category = matchedRule.categoryName.toLowerCase();

      if (category.includes("infant")) return "infant";
      if (category.includes("senior")) return "senior";
      if (category.includes("adult")) return "adult";
      if (category.includes("child")) return "child";

      return "adult";
    },
    [ageRules],
  );

  // CALL API AND SET PREFIELD DATA FROM ITS
  const fetchItsVisaDetails = useCallback(
    async (applicant: Applicant) => {
      if (!applicant.itsNo || applicant.itsNo.length !== 8) return;

      setIsSearching(true);

      try {
        const info = await AddpaxgetDetailsByITS(applicant.itsNo, true);

        if (!info?.mumin_Id && !info?.muminDataId) {
          setApplicants((prev) =>
            prev.map((a) =>
              a.id === applicant.id
                ? {
                    ...createEmptyApplicant(),
                    id: a.id,
                    itsNo: a.itsNo,
                    formErrors: {
                      itsNo: "No record found for this ITS number.",
                    },
                    hasFetched: true,
                  }
                : a,
            ),
          );
          return;
        }

        const calculatedAge = info.age
          ? Number(info.age)
          : info.dob
            ? Number(calculateAge(info.dob))
            : 0;
        const applicantType = getApplicantTypeFromAge(calculatedAge);

        // ✅ Populate applicant data
        setApplicants((prev) =>
          prev.map((a) =>
            a.id === applicant.id
              ? {
                  ...a,
                  title: info?.prefix ?? "",
                  fullName: info?.fullName ?? "",
                  firstName: info.first_Name ?? "",
                  middleName: info.middle_Name ?? "",
                  lastName: info.surname ?? "",
                  dob: info.dob ? info.dob.split("T")[0] : "",
                  // age: info.age ? info?.age : info.dob ? calculateAge(info.dob) : "",
                  age: calculatedAge.toString(),
                  applicantType,
                  gender: mapGender(info.gender),
                  birthPlace: info.jamiaat ?? "",
                  hofIts: info.hoF_ID ?? "",
                  nationality: info.nationality ?? "",
                  category: info.category ?? "",
                  hasFetched: true,
                }
              : a,
          ),
        );
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "ITS API failed";
        toast.error(errorMessage);
      } finally {
        setIsSearching(false);
      }
    },
    [
      createEmptyApplicant,
      setApplicants,
      setIsSearching,
      getApplicantTypeFromAge,
    ],
  );

  useEffect(() => {
    applicants.forEach((applicant) => {
      if (
        applicant.itsNo?.length === 8 &&
        !applicant.hasFetched &&
        !applicant.isNonItsUser
      ) {
        fetchItsVisaDetails(applicant);
      }
    });
  }, [applicants, fetchItsVisaDetails]);
  // FETCH API DATA USING ITSNO

  const handleAddNewApplicant = () => {
    const newApplicant = initializeApplicant();
    setApplicants([...applicants, newApplicant]);
    toast.success("New applicant form added");
  };

  const handleDeleteApplicant = (id: string) => {
    if (applicants.length === 1) {
      toast.error("At least one applicant form is required");
      return;
    }
    setApplicants(applicants.filter((a) => a.id !== id));
    toast.success("Applicant form removed");
  };

  const handleToggleExpand = (id: string) => {
    setApplicants(
      applicants.map((a) =>
        a.id === id ? { ...a, isExpanded: !a.isExpanded } : a,
      ),
    );
  };

  const updateApplicant = (
    id: string,
    field: keyof Applicant,
    value: unknown,
  ) => {
    setApplicants((prev) =>
      prev.map((applicant) => {
        if (applicant.id !== id) return applicant;
        // When Non-ITS User toggles checked unchecked → reset basic info
        if (field === "isNonItsUser") {
          return {
            ...applicant,
            isNonItsUser: Boolean(value),
            itsNo: Boolean(value) ? "99999999" : "",
            title: "",
            firstName: "",
            fullName: "",
            middleName: "",
            lastName: "",
            dob: "",
            age: "0",
            gender: "",
            birthPlace: "",
            nationality: "",
            applicantType: "adult",
            category: "",
            guardianIts: "",
            hofIts: "",
            formErrors: {},
            validationErrors: [],
            showValidationErrors: false, // optional: clear ITS
            hasFetched: false, // reset ITS fetch flag
          };
        }
        // Normal field update
        return {
          ...applicant,
          [field]: value,
          ...(field === "itsNo" && {
            hasFetched: false,
            formErrors: {
              ...applicant.formErrors,
              itsNo: undefined,
            },
          }),
          ...(field === "title" && {
            formErrors: {
              ...applicant.formErrors,
              title: undefined,
            },
          }),
        };
      }),
    );
  };

  const calculateAge = (dob: string) => {
    if (!dob) return "0";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age.toString();
  };

  const handleDateOfBirthChange = (id: string, value: string) => {
    const selectedDate = new Date(value);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setApplicants((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                formErrors: {
                  ...a.formErrors,
                  dob: "Future date is not allowed",
                },
              }
            : a,
        ),
      );
      return;
    }

    const age = Number(calculateAge(value));
    const applicantType = getApplicantTypeFromAge(age);

    setApplicants((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              dob: value,
              age: age.toString(),
              applicantType,
              formErrors: {
                ...a.formErrors,
                dob: undefined,
              },
            }
          : a,
      ),
    );
  };

  const handleDocumentUpload = async (
    applicantId: string,
    documentType: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const sizeInMB = file.size / (1024 * 1024);

    // Find applicant & document config
    const applicant = applicants.find((a) => a.id === applicantId);
    const docConfig = applicant?.documents.find(
      (d) => d.documentType === documentType,
    );

    // ---------------- VALIDATIONS ----------------
    if (docConfig) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      const acceptedFormatsList = (docConfig.acceptedFormatsList || []).map(
        (f) => f.toLowerCase(),
      );

      if (
        acceptedFormatsList.length > 0 &&
        !acceptedFormatsList.includes(fileExtension)
      ) {
        toast.error(
          `Only ${acceptedFormatsList.join(", ")} files are allowed for ${documentType}`,
        );
        return;
      }

      const maxSizeMB = Number(docConfig.maxSize) / 1024 || 5;

      if (sizeInMB > maxSizeMB) {
        toast.error(`File size should not exceed ${maxSizeMB}MB`);
        return;
      }
    }

    const shouldScanPassport =
      docConfig?.documentCode?.trim().toUpperCase() === "PASSPORT" ||
      documentType.trim().toLowerCase() === "passport copy";

    const passportOcrData = shouldScanPassport
      ? await scanPassport(file, documentType, docConfig?.documentCode)
      : null;

    if (shouldScanPassport && !passportOcrData) {
      e.target.value = "";
      return;
    }

    // ---------------- SAVE FILE ----------------
    setApplicants((prev) =>
      prev.map((a) => {
        if (a.id !== applicantId) return a;

        // 1️ Update document
        const updatedDocuments = a.documents.map((doc) =>
          doc.documentType === documentType
            ? {
                ...doc,
                fileName: file.name,
                file,
                uploadedDate: new Date().toISOString(),
              }
            : doc,
        );

        // 2️ Check if still missing mandatory docs
        const stillMissingMandatoryDocs = updatedDocuments.some(
          (doc) => doc.mandatory && !doc.file,
        );

        // 3️ Remove document error if all mandatory docs uploaded
        const updatedFormErrors = { ...a.formErrors };

        if (!stillMissingMandatoryDocs) {
          delete updatedFormErrors?.documents;
        }

        return {
          ...a,
          documents: updatedDocuments,
          formErrors: updatedFormErrors,
        };
      }),
    );

    setTimeout(() => {
      toast.success(`${documentType} uploaded successfully`);
      e.target.value = "";
    }, 500);

    if (!shouldScanPassport) return;

    // ----------------OCR SCAN PASSPORT EXTRACTION ----------------
    try {
      const ocrData = passportOcrData;
      if (ocrData) {
        setApplicants((prev) =>
          prev.map((a) =>
            a.id === applicantId
              ? {
                  ...a,
                  passportNo: ocrData.passportNumber ?? "",
                  passportType: getPassportTypeName(ocrData.passportType),
                  passportIssuePlace: ocrData?.placeOfIssue ?? "",
                  passportIssueDate: normalizeDate(ocrData?.issueDate) ?? "",
                  passportExpiryDate: normalizeDate(ocrData.expiryDate) ?? "",
                  passportDob: normalizeDate(ocrData.dateOfBirth ?? ""),
                  passportArabicName: ocrData.arabicName ?? "",
                  // passportDob: ocrData.dateOfBirth ? normalizeDate(ocrData.dateOfBirth.split("-").reverse().join("-")) : "",
                  passportFirstName: ocrData.firstName ?? "",
                  passportMiddleName: ocrData.middleName ?? "",
                  passportLastName: ocrData.lastName ?? "",
                  passportGender:
                    ocrData?.gender?.toLocaleLowerCase() === "m"
                      ? "Male"
                      : "Female",
                  passportNationality:
                    ocrData.nationality ?? "All Nationalities",
                  // hasFetched: true,
                }
              : a,
          ),
        );
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message =
          error.response.data.message || "Failed to add qarzan type";
        toast.error(
          message ?? "Something went wrong while processing the document",
        );
      }
    }
    // ----------------OCR SCAN PASSPORT EXTRACTION ----------------
  };

  const handleRemoveDocument = (applicantId: string, documentType: string) => {
    setApplicants((prev) =>
      prev.map((applicant) => {
        if (applicant.id === applicantId) {
          return {
            ...applicant,
            documents: applicant.documents.map((doc) =>
              doc.documentType === documentType
                ? {
                    documentType: doc.documentType,
                    mandatory: doc.mandatory,
                    acceptedFormatsList: doc.acceptedFormatsList,
                    maxSize: doc.maxSize,
                    screenNames: doc.screenNames,
                    documentMasterId: doc.documentMasterId,
                  }
                : doc,
            ),
          };
        }
        return applicant;
      }),
    );

    toast.success(`${documentType} removed`);
  };

  const handleViewDocument = (file?: File) => {
    if (!file) {
      toast.error("No document available");
      return;
    }
    const url = URL.createObjectURL(file);
    window.open(url, "_blank");
  };

  // const validateTextField = (value: string) => {
  //   if (!value) return "";

  //   if (value.length < 3)
  //     return "Minimum length is 3";

  //   if (value.length > 100)
  //     return "Maximum length is 100";

  //   if (!/^[A-Za-z ]+$/.test(value))
  //     return "Only alphabets are allowed";

  //   return "";
  // };

  const validateLongTextField = (value: string) => {
    if (!value) return "";

    if (value.length < 10) return "Minimum length is 10";

    if (value.length > 1000) return "Maximum length is 1000";

    return "";
  };

  // const validatePassportArabicName = (value: string) => {
  //   if (!value) return "";

  //   if (value.length < 3)
  //     return "Minimum length is 3";

  //   if (value.length > 300)
  //     return "Maximum length is 300";

  //   return "";
  // };

  const validateFamilyITS = (value: string) => {
    if (!value) return "";

    if (!/^\d{8}$/.test(value)) return "ITS number must be exactly 8 digits";

    return "";
  };

  // const validateFamilyName = (value: string) => {
  //   if (!value) return "";

  //   if (value.length < 3)
  //     return "Minimum length is 3";

  //   if (value.length > 200)
  //     return "Maximum length is 200";

  //   if (!/^[A-Za-z ]+$/.test(value))
  //     return "Only alphabets are allowed";

  //   return "";
  // };

  // const validateFamilyArabicName = (value: string) => {
  //   if (!value) return "";

  //   if (value.length < 3)
  //     return "Minimum length is 3";

  //   if (value.length > 300)
  //     return "Maximum length is 300";

  //   if (!/^[\u0600-\u06FF\s]+$/.test(value))
  //     return "Only Arabic letters are allowed";

  //   return "";
  // };

  // REQUIRED FORM FIELD ERROR HANDLING
  const validateRequiredFields = (
    applicant: Applicant,
  ): ApplicantFormErrors => {
    const errors: ApplicantFormErrors = {};
    // Validate required documents
    if (!applicant.itsNo && !applicant.isNonItsUser)
      errors.itsNo = "ITS number is required";
    // if (!applicant.title) errors.title = "Title is required";
    if (!applicant.firstName) errors.firstName = "First name is required";
    if (!applicant.lastName) errors.lastName = "Last name is required";
    if (applicant.isNonItsUser && !applicant.dob)
      errors.dob = "Date of birth is required";
    if (applicant.dob) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dob = new Date(applicant.dob);

      if (dob > today) {
        errors.dob = "Future date is not allowed";
      }
    }
    if (applicant.isNonItsUser && !applicant.gender)
      errors.gender = "Gender is required";
    if (!applicant.passportGender) {
      errors.passportGender = "Passport gender is required";
    }
    if (!applicant.nationality) errors.nationality = "Nationality is required";
    if (!applicant.applicantType)
      errors.applicantType = "Applicant type is required";
    // if (!applicant.category) errors.category = "Category is required";

    if (!applicant.passportNo) {
      errors.passportNo = "Passport number is required";
    } else if (!/^[A-Za-z0-9]{5,12}$/.test(applicant.passportNo)) {
      errors.passportNo =
        "Passport number must be between 5 and 12 alphanumeric characters.";
    }

    if (!applicant.passportIssuePlace)
      errors.passportIssuePlace = "Passport issue place is required";
    if (!applicant.passportIssueDate) {
      errors.passportIssueDate = "Passport issue date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const issueDate = new Date(applicant.passportIssueDate);

      if (issueDate > today) {
        errors.passportIssueDate = "Future date is not allowed";
      }
    }
    if (!applicant.passportExpiryDate) {
      errors.passportExpiryDate = "Passport Expiry Date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const expiryDate = new Date(applicant.passportExpiryDate);

      if (expiryDate < today) {
        errors.passportExpiryDate = "Past date is not allowed";
      }
    }

    if (!applicant.passportType)
      errors.passportType = "Passport type is required";
    if (!applicant.passportFirstName)
      errors.passportFirstName = "Passport first name is required";
    if (!applicant.passportDob) {
      errors.passportDob = "Passport DOB is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const passportDob = new Date(applicant.passportDob);

      if (passportDob > today) {
        errors.passportDob = "Future date is not allowed";
      }
    }
    if (!applicant.passportNationality)
      errors.passportNationality = "Passport Nationality is required";
    if (!applicant.passportLastName)
      errors.passportLastName = "Passport last name is required";

    if (!applicant.visaRequired && !applicant.visaNotRequiredRemark) {
      errors.visaNotRequiredRemark = "Please provide a visa remark";
    }
    // Validate required documents

    // Document validation
    const hasMissingMandatoryDocs = applicant.documents.some(
      (doc) => doc.mandatory && !doc.fileName,
    );

    if (hasMissingMandatoryDocs) {
      errors.documents = "Please upload all required documents";
    }

    // const firstNameError = validateTextField(applicant.firstName);
    // if (firstNameError) errors.firstName = firstNameError;

    // const middleNameError = validateTextField(applicant.middleName);
    // if (middleNameError) errors.middleName = middleNameError;

    // const lastNameError = validateTextField(applicant.lastName);
    // if (lastNameError) errors.lastName = lastNameError;

    // const birthPlaceError = validateTextField(applicant.birthPlace);
    // if (birthPlaceError) errors.birthPlace = birthPlaceError;

    // const nationalityError = validateTextField(applicant.nationality);
    // if (nationalityError) errors.nationality = nationalityError;

    // const categoryError = validateTextField(applicant.category);
    // if (categoryError) errors.category = categoryError;

    // const professionError = validateTextField(applicant.profession);
    // if (professionError) errors.profession = professionError;

    // const passportIssuePlaceError = validateTextField(applicant.passportIssuePlace ?? "");
    // if (passportIssuePlaceError)
    //   errors.passportIssuePlace = passportIssuePlaceError;

    // const passportFirstNameError = validateTextField(applicant.passportFirstName);
    // if (passportFirstNameError)
    //   errors.passportFirstName = passportFirstNameError;

    // const passportMiddleNameError = validateTextField(applicant.passportMiddleName);
    // if (passportMiddleNameError)
    //   errors.passportMiddleName = passportMiddleNameError;

    // const passportLastNameError = validateTextField(applicant.passportLastName);
    // if (passportLastNameError)
    //   errors.passportLastName = passportLastNameError;

    // const passportArabicNameError = validatePassportArabicName(
    //   applicant.passportArabicName
    // );

    // if (passportArabicNameError) {
    //   errors.passportArabicName = passportArabicNameError;
    // }

    const fatherItsError = validateFamilyITS(applicant.fatherItsNo ?? "");
    if (fatherItsError) errors.fatherItsNo = fatherItsError;

    const grandfatherItsError = validateFamilyITS(
      applicant.grandfatherItsNo ?? "",
    );
    if (grandfatherItsError) errors.grandfatherItsNo = grandfatherItsError;

    const motherItsError = validateFamilyITS(applicant.motherItsNo ?? "");
    if (motherItsError) errors.motherItsNo = motherItsError;

    const mothersFatherItsError = validateFamilyITS(
      applicant.mothersFatherItsNo ?? "",
    );
    if (mothersFatherItsError)
      errors.mothersFatherItsNo = mothersFatherItsError;

    // const fatherNameError = validateFamilyName(applicant.fatherName ?? "");
    // if (fatherNameError)
    //   errors.fatherName = fatherNameError;

    // const grandfatherNameError = validateFamilyName(applicant.grandfatherName ?? "");
    // if (grandfatherNameError)
    //   errors.grandfatherName = grandfatherNameError;

    // const motherNameError = validateFamilyName(applicant.motherName ?? "");
    // if (motherNameError)
    //   errors.motherName = motherNameError;

    // const mothersFatherNameError = validateFamilyName(applicant.mothersFatherName ?? "");
    // if (mothersFatherNameError)
    //   errors.mothersFatherName = mothersFatherNameError;

    // const fatherArabicError = validateFamilyArabicName(applicant.fatherNameArabic ?? "");
    // if (fatherArabicError)
    //   errors.fatherNameArabic = fatherArabicError;

    // const grandfatherArabicError = validateFamilyArabicName(applicant.grandfatherNameArabic ?? "");
    // if (grandfatherArabicError)
    //   errors.grandfatherNameArabic = grandfatherArabicError;

    // const motherArabicError = validateFamilyArabicName(applicant.motherNameArabic ?? "");
    // if (motherArabicError)
    //   errors.motherNameArabic = motherArabicError;

    // const mothersFatherArabicError = validateFamilyArabicName(applicant.mothersFatherNameArabic ?? "");
    // if (mothersFatherArabicError)
    //   errors.mothersFatherNameArabic = mothersFatherArabicError;

    const remarksError = validateLongTextField(applicant.remarks);

    if (remarksError) {
      errors.remarks = remarksError;
    }

    return errors;
  };

  const validateApplicant = (applicant: Applicant): FieldValidation[] => {
    const errors: FieldValidation[] = [];

    // Compare First Name
    if (applicant.passportFirstName && applicant.firstName) {
      const isValid =
        applicant.passportFirstName.toLowerCase().trim() ===
        applicant.firstName.toLowerCase().trim();
      errors.push({
        field: "firstName",
        itsValue: applicant.firstName,
        passportValue: applicant.passportFirstName,
        isValid,
        remark: "",
      });
    }

    // Compare Middle Name
    if (applicant.passportMiddleName && applicant.middleName) {
      const isValid =
        applicant.passportMiddleName.toLowerCase().trim() ===
        applicant.middleName.toLowerCase().trim();
      errors.push({
        field: "middleName",
        itsValue: applicant.middleName,
        passportValue: applicant.passportMiddleName,
        isValid,
        remark: "",
      });
    }

    // Compare Last Name
    if (applicant.passportLastName && applicant.lastName) {
      const isValid =
        applicant.passportLastName.toLowerCase().trim() ===
        applicant.lastName.toLowerCase().trim();
      errors.push({
        field: "lastName",
        itsValue: applicant.lastName,
        passportValue: applicant.passportLastName,
        isValid,
        remark: "",
      });
    }

    // Compare Date of Birth
    if (applicant.passportDob && applicant.dob) {
      const isValid = applicant.passportDob === applicant.dob;
      errors.push({
        field: "dob",
        itsValue: applicant.dob,
        passportValue: applicant.passportDob,
        isValid,
        remark: "",
      });
    }

    // Compare Gender
    if (applicant.passportGender && applicant.gender) {
      const isValid =
        applicant.passportGender.toLowerCase() ===
        applicant.gender.toLowerCase();
      errors.push({
        field: "gender",
        itsValue: applicant.gender,
        passportValue: applicant.passportGender,
        isValid,
        remark: "",
      });
    }

    // Compare Nationality
    // if (applicant.passportNationality && applicant.nationality) {
    //   const isValid = applicant.passportNationality.toLowerCase().trim() === applicant.nationality.toLowerCase().trim();
    //   errors.push({
    //     field: 'nationality',
    //     itsValue: applicant.nationality,
    //     passportValue: applicant.passportNationality,
    //     isValid,
    //     remark: ''
    //   });
    // }

    return errors;
  };

  //CHEAKIN
  // G API ERROR AND SET
  const mapApiMessagesToFields = (messages: string[]): FieldErrorMap => {
    const errors: FieldErrorMap = {};
    messages.forEach((msg) => {
      if (msg.includes("Category")) errors.category = msg;
      if (msg.includes("ITS number")) errors.itsNo = msg;
      if (msg.includes("Passport number")) errors.passportNo = msg;
      if (msg.includes("Passport type")) errors.passportType = msg;
      if (msg.includes("Passport first name")) errors.passportFirstName = msg;
      if (msg.includes("Passport last name")) errors.passportLastName = msg;
    });
    return errors;
  };

  const handleConfirmAction = async () => {
    try {
      setIsSubmitting(true);
      await handleApplyForVisa(false);
    } finally {
      setConfirmDialogOpen(false);
      setPendingAction(null);
      setIsSubmitting(false);
    }
  };

  const handleApplyForVisa = async (isDraft = false) => {
    // let selectedApplicants: Applicant[];

    // if (applicants.length === 1) {
    //   selectedApplicants = applicants;
    // } else {
    //   selectedApplicants = applicants.filter(a => a.isSelected);

    //   if (selectedApplicants.length === 0) {
    //     toast.error("Please select at least one applicant for visa processing");
    //     return;
    //   }
    // }

    const selectedApplicants = applicants;
    /* -------------------------------------------------
       1️⃣ REQUIRED FIELD VALIDATION
    --------------------------------------------------*/
    let hasRequiredFieldErrors = false;

    // const applicantsAfterRequiredValidation = applicants.map(applicant => {
    //   const shouldValidate = applicants.length === 1 || applicant.isSelected;
    //   if (!shouldValidate) return applicant;
    //   const formErrors = validateRequiredFields(applicant);

    //   if (Object.keys(formErrors).length > 0) {
    //     hasRequiredFieldErrors = true;
    //   }

    //   return {
    //     ...applicant,
    //     formErrors
    //   };
    // });

    const applicantsAfterRequiredValidation = applicants.map((applicant) => {
      const formErrors = validateRequiredFields(applicant);
      console.log("formErrors", formErrors);

      if (Object.keys(formErrors).length > 0) {
        hasRequiredFieldErrors = true;
      }

      return {
        ...applicant,
        formErrors,
      };
    });

    setApplicants(applicantsAfterRequiredValidation);

    if (hasRequiredFieldErrors) {
      toast.error("Please fill all required fields");
      return;
    }

    /* -------------------------------------------------
       2️⃣ COMPARISON VALIDATION
    --------------------------------------------------*/
    let hasValidationErrors = false;

    const validatedApplicants = applicantsAfterRequiredValidation.map(
      (applicant) => {
        // const shouldValidate = applicants.length === 1 || applicant.isSelected;

        // if (!shouldValidate) return applicant;

        let validationErrors = validateApplicant(applicant);

        validationErrors = validationErrors.map((v) => {
          const old = applicant.validationErrors?.find(
            (x) => x.field === v.field,
          );
          return {
            ...v,
            remark: old?.remark || "",
            isValid: old?.remark?.trim() ? true : v.isValid,
          };
        });

        if (validationErrors.some((v) => !v.isValid)) {
          hasValidationErrors = true;
        }

        return {
          ...applicant,
          validationErrors,
          showValidationErrors: true,
        };
      },
    );

    setApplicants(validatedApplicants);

    // if (!isDraft && hasValidationErrors) {
    if (hasValidationErrors) {
      toast.error(
        "Validation errors found! Please correct the mismatches or add remarks to proceed.",
      );
      return;
    }

    /* -------------------------------------------------
        BUILD APPLICANTS JSON (REQUIRED BY BACKEND)
     --------------------------------------------------*/
    const applicantsPayload = selectedApplicants.map((applicant, aIdx) => ({
      tourOperatorId: Number(applicant.tourOperatorId ?? user?.tourOperatorId),
      isNonItsUser: Boolean(applicant.isNonItsUser),
      ejamaatId: applicant.ejamaatId ?? "",
      itsNo: applicant.itsNo,
      category: applicant.category,
      applicantType:
        applicant.applicantType === "adult" ? "Adult" : applicant.applicantType,
      title: applicant.title,
      firstName: applicant.firstName,
      middleName: applicant.middleName ?? "",
      lastName: applicant.lastName,
      // fullName:applicant.fullName,
      toFullName: applicant.fullName,
      age: Number(applicant.age),
      profession: applicant.profession ?? "",
      nationality: applicant.nationality,
      gender: applicant.gender,

      dateOfBirth: applicant.dob || null,

      passportIssuePlace: applicant.passportIssuePlace ?? "",
      passportType: applicant.passportType ?? "",
      passportNo: applicant.passportNo ?? "",
      passportDOB: applicant.passportDob || null,
      passportFirstName: applicant.passportFirstName,
      passportMiddleName: applicant.passportMiddleName ?? "",
      passportLastName: applicant.passportLastName,
      passportArabicName: applicant.passportArabicName ?? "",
      passportNationality: applicant.passportNationality ?? "",
      passportExpiryDate: applicant.passportExpiryDate || null,
      passportGender: applicant.passportGender ?? "",
      passportIssueDate: applicant.passportIssueDate || null,

      birthPlace: applicant.birthPlace,
      guardianITS: applicant.guardianIts ?? "",
      visaRequired: forceVisaRequired
        ? false
        : Boolean(applicant.visaRequired),
      hofITS: applicant.hofIts ?? "",

      fatherITSNo: applicant.fatherItsNo ?? "",
      fatherName: applicant.fatherName ?? "",
      fatherNameArabic: applicant.fatherNameArabic ?? "",

      motherITSNo: applicant.motherItsNo ?? "",
      motherName: applicant.motherName ?? "",
      motherNameArabic: applicant.motherNameArabic ?? "",

      mothersFatherITSNo: applicant.mothersFatherItsNo ?? "",
      mothersFatherName: applicant.mothersFatherName ?? "",
      mothersFatherNameArabic: applicant.mothersFatherNameArabic ?? "",

      grandfatherITSNo: applicant.grandfatherItsNo ?? "",
      grandfatherName: applicant.grandfatherName ?? "",
      grandfatherNameArabic: applicant.grandfatherNameArabic ?? "",
      remarks: applicant.remarks,
      visaNotRequiredRemark: applicant.visaNotRequiredRemark ?? "",
      isMiqaatApplicant: Boolean(applicant.isMiqaatApplicant),
      ValidationErrors: applicant.validationErrors
        ? JSON.stringify(applicant.validationErrors)
        : null,
      submit: isDraft ? false : true, // test-safe

      documents: applicant.documents
        .filter((doc) => doc.file instanceof File)
        .map((doc, dIdx) => ({
          documentMasterId: Number(doc.documentMasterId) ?? 0,
          // fileKey: `app-${doc.documentMasterId}-doc-${doc.documentMasterId}`
          fileKey: `app-${aIdx}-doc-${doc.documentMasterId}`,
        })),
    }));

    /* -------------------------------------------------
       CREATE FORM DATA
    --------------------------------------------------*/
    const formData = new FormData();
    formData.append("isDraft", String(isDraft));
    formData.append("Applicants", JSON.stringify(applicantsPayload));
    /* -------------------------------------------------
       ATTACH FILES USING fileKey
    --------------------------------------------------*/
    selectedApplicants.forEach((applicant, aIdx) => {
      applicant.documents.forEach((doc, index) => {
        if (doc.file instanceof File) {
          const key = `app-${aIdx}-doc-${doc.documentMasterId}`;
          formData.append(`Files`, doc.file, key);
          formData.append(`FileMeta[${key}].OriginalName`, doc.file.name);
          formData.append(`FileMeta[${key}].ContentType`, doc.file.type);
        }
      });
    });

    /* -------------------------------------------------
      API CALL
    --------------------------------------------------*/
 try {
  setIsSubmitting(true);

  if (onSave) {
    const response = await SaveWalkInVisaAppDraft(formData);

    const createdApplicants = response?.applicants ?? [];

    for (const applicant of selectedApplicants) {
      const createdApplicant = createdApplicants.find(
        (item: { itsNo: string; visaApplicationId: number }) =>
          String(item.itsNo) === String(applicant.itsNo),
      );

      if (!createdApplicant?.visaApplicationId) {
        throw new Error(
          `Visa application ID not returned for ITS ${applicant.itsNo}`,
        );
      }

      await onSave({
        ...applicant,
        id: String(createdApplicant.visaApplicationId),
        visaRequired: false,
        tourOperatorId: Number(
          tourOperatorId ?? applicant.tourOperatorId ?? 0,
        ),
      });
    }

    toast.success("Applicant added successfully");
    onClose?.();
    return;
  }

  await SaveWalkInVisaAppDraft(formData);

  toast.success(
    isDraft
      ? "Visa application saved as draft"
      : `Visa application submitted for ${selectedApplicants.length} applicant(s)`,
  );
} catch (error) {
  console.log("error", error);

  if (axios.isAxiosError(error)) {
    const apiData = error.response?.data as {
      message?: string;
      errors?: Array<{
        itsNumber?: string;
        errors: string[];
      }>;
    };

    if (apiData?.errors?.length) {
      const message = apiData.errors
        .map(
          (e) =>
            `ITS ${e.itsNumber}: ${e.errors.join(", ")}`,
        )
        .join("\n");

      toast.error(message);

      setApplicants((prevApplicants) =>
        prevApplicants.map((applicant) => {
          const backendError = apiData?.errors?.find(
            (e) => e.itsNumber === applicant.itsNo,
          );

          if (!backendError) {
            return {
              ...applicant,
              formErrors: {},
            };
          }

          return {
            ...applicant,
            formErrors: mapApiMessagesToFields(
              backendError.errors,
            ),
          };
        }),
      );

      return;
    }

    if (apiData?.message) {
      toast.error(apiData.message);
      return;
    }
  }

  toast.error("Something went wrong");
} finally {
  setIsSubmitting(false);
}
     
  };

  const updateValidationRemark = (
    applicantId: string,
    field: string,
    remark: string,
  ) => {
    setApplicants(
      applicants.map((a) => {
        if (a.id === applicantId && a.validationErrors) {
          const updatedErrors = a.validationErrors.map((v) =>
            v.field === field ? { ...v, remark } : v,
          );
          return { ...a, validationErrors: updatedErrors };
        }
        return a;
      }),
    );
  };

  const handleRemarkBlur = (
    applicantId: string,
    field: string,
    remark: string,
  ) => {
    if (remark.trim()) {
      const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
      toast.success(
        `✓ Remark accepted for ${fieldName}. This validation error will be ignored.`,
        { duration: 3000 },
      );
    }
  };

  const handleDownloadTemplate = () => {
    const templateType =
      importType === "family" ? "Family Import" : "Individual Import";
    toast.success(`Downloading ${templateType} Excel template...`);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    // In real app, parse Excel file and populate applicants
    toast.success(`Importing data from ${file.name}...`);

    // Mock: Add sample imported data
    // const mockImportedData: Applicant[] = importType === 'family'
    //   ? [
    //     {
    //       ...createEmptyApplicant(),
    //       firstName: 'Ahmed',
    //       lastName: 'Khan',
    //       title: 'Mr',
    //       gender: 'Male',
    //       dob: '1985-05-15',
    //       age: '39',
    //       nationality: 'Indian',
    //       itsNo: '12345678',
    //       isExpanded: false,
    //       isSelected: true
    //     },
    //   ];

    // setApplicants([...applicants, ...mockImportedData]);
    setShowImportDialog(false);
    // toast.success(`Successfully imported ${mockImportedData.length} applicant(s) from Excel`);
    e.target.value = "";
  };

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-white">
      {loading && <FullScreenLoading />}
      {ocrLoading && <FullScreenLoading title="Scanning Passport..." />}

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              Import Applicant Data
            </DialogTitle>
            <DialogDescription>
              Import applicants from Excel file - Family-wise or Individual
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Import Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm">Import Type</Label>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={importType === "individual" ? "default" : "outline"}
                  onClick={() => setImportType("individual")}
                  className={`h-auto py-4 flex flex-col gap-2 ${
                    importType === "individual"
                      ? "bg-[#5B9BD5] text-white"
                      : "border-gray-300 hover:border-[#5B9BD5]"
                  }`}
                >
                  <User className="w-6 h-6" />
                  <div>
                    <div className="text-sm">Individual Applicant</div>
                    <div className="text-xs opacity-70">
                      Import single applicants
                    </div>
                  </div>
                </Button>
                <Button
                  variant={importType === "family" ? "default" : "outline"}
                  onClick={() => setImportType("family")}
                  className={`h-auto py-4 flex flex-col gap-2 ${
                    importType === "family"
                      ? "bg-[#5B9BD5] text-white"
                      : "border-gray-300 hover:border-[#5B9BD5]"
                  }`}
                >
                  <UsersIcon className="w-6 h-6" />
                  <div>
                    <div className="text-sm">Family Group</div>
                    <div className="text-xs opacity-70">
                      Import family members
                    </div>
                  </div>
                </Button>
              </div>
            </div>

            {/* Download Template */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm text-blue-900">
                    Step 1: Download Template
                  </h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Download the Excel template for{" "}
                    {importType === "family"
                      ? "family import"
                      : "individual import"}{" "}
                    and fill in the applicant details.
                  </p>
                  <Button
                    variant="link"
                    onClick={handleDownloadTemplate}
                    className="text-blue-600 hover:text-blue-700 px-0 mt-2 h-auto"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download {importType === "family"
                      ? "Family"
                      : "Individual"}{" "}
                    Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Upload File */}
            <div className="space-y-3">
              <Label className="text-sm">
                Step 2: Upload Filled Excel File
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#5B9BD5] transition-colors">
                <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600 mb-3">
                  Click to upload or drag and drop
                </p>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFile}
                  className="hidden"
                  id="import-file-input"
                />
                <Label
                  htmlFor="import-file-input"
                  className="inline-flex items-center px-4 py-2 bg-[#5B9BD5] text-white rounded-lg cursor-pointer hover:opacity-90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Excel File
                </Label>
                <p className="text-xs text-gray-500 mt-2">
                  Supports: .xlsx, .xls
                </p>
              </div>
            </div>

            {/* Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> The imported data will be added to your
                current applicant list. Review all details before submitting.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applicant Cards */}
      <div className="space-y-4 mb-6">
        {applicants.map((applicant, index) => (
          <Card
            key={applicant.id}
            className="bg-white shadow-lg border-[#5B9BD5]/20 rounded-2xl overflow-hidden"
            style={{ gap: "0px" }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 border-b border-gray-200 p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                  onClick={() => handleToggleExpand(applicant.id)}
                >
                  <Badge className="bg-[#5B9BD5] text-white shrink-0">
                    Applicant {index + 1}
                  </Badge>
                  {applicant.firstName && (
                    <span className="text-sm text-gray-700 truncate">
                      {applicant.title} {applicant.firstName}{" "}
                      {applicant.middleName} {applicant.lastName}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4">
                  {/* <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={applicant.isSelected}
                      onCheckedChange={(checked) => updateApplicant(applicant.id, 'isSelected', checked)}
                      id={`select-${applicant.id}`}
                    />
                    <Label htmlFor={`select-${applicant.id}`} className="text-xs md:text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                      Select for Processing
                    </Label>
                  </div> */}

                  <div
                    onClick={() => handleToggleExpand(applicant.id)}
                    className="cursor-pointer shrink-0"
                  >
                    {applicant.isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Card Content */}
            {applicant.isExpanded && (
              <CardContent className="p-4 md:p-6">
                <div className="space-y-6">
                  {/* ITS Number & Login ID */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      {/* <Label className="text-sm text-gray-700">ITS Number<span className="text-red-500">*</span></Label> */}
                      <InputFieldset
                        label="ITS Number"
                        required
                        value={applicant.itsNo}
                        readOnly={applicant.isNonItsUser}
                        onChange={(e) =>
                          updateApplicant(applicant.id, "itsNo", e.target.value)
                        }
                        placeholder="Enter 8-digit ITS number"
                        maxLength={8}
                        className={`border-gray-300 rounded-lg ${
                          applicant.isNonItsUser
                            ? "cursor-not-allowed bg-gray-100"
                            : ""
                        }`}
                      />
                      {isSearching && (
                        <p className="text-sky-600 text-sm mt-1 flex items-center gap-1">
                          <Search className="h-3 w-3 animate-pulse" />
                          Fetching mumeen data...
                        </p>
                      )}
                      {applicant.formErrors?.itsNo && (
                        <p className="text-red-500 text-xs mt-1">
                          {applicant.formErrors.itsNo}
                        </p>
                      )}
                    </div>

                    {applicant.fullName && (
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Full Name</Label> */}
                        {/* <Input
                        value={applicant.fullName}
                        readOnly={applicant.hasFetched && Boolean(applicant.fullName)}
                        onChange={(e) => updateApplicant(applicant.id, 'fullName', e.target.value)}
                        className={`border-gray-300 rounded-lg ${applicant.hasFetched ? 'cursor-not-allowed' : ''}`}
                      /> */}

                        <InputFieldset
                          label="Full Name"
                          value={applicant.fullName}
                          // readOnly={applicant.fullName}
                          onChange={(e) =>
                            updateApplicant(
                              applicant.id,
                              "fullName",
                              e.target.value,
                            )
                          }
                          className={`border-gray-300 rounded-lg1`}
                          // className={`border-gray-300 rounded-lg ${applicant.fullName
                          // ? "cursor-not-allowed bg-gray-100"
                          // : ""
                          // }`}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">User Type</Label>
                      <div className="flex items-center gap-3 h-10">
                        <Checkbox
                          id={`non-its-${applicant.id}`}
                          checked={applicant.isNonItsUser}
                          onCheckedChange={(checked) =>
                            updateApplicant(
                              applicant.id,
                              "isNonItsUser",
                              checked,
                            )
                          }
                        />
                        <label
                          htmlFor={`non-its-${applicant.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Non ITS User
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Basic Information Section */}
                  <div className="space-y-4">
                    <h3
                      className="text-sm flex items-center gap-2"
                      style={{ color: "#5B9BD5" }}
                    >
                      <User className="w-4 h-4" />
                      Basic Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Title</Label> */}
                        <SelectFieldset label="Title">
                          <Select
                            value={applicant.title}
                            disabled={
                              applicant.hasFetched && Boolean(applicant.title)
                            }
                            onValueChange={(value) =>
                              updateApplicant(applicant.id, "title", value)
                            }
                          >
                            <SelectTriggerUI
                              className={cn(
                                applicant.hasFetched && "cursor-not-allowed",
                              )}
                            >
                              <SelectValue placeholder="Select" />
                            </SelectTriggerUI>
                            <SelectContent>
                              {titles.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                        {/* {applicant.formErrors?.title && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.title}
                          </p>
                        )} */}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">First Name <span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="First Name"
                          required
                          value={applicant.firstName}
                          readOnly={
                            applicant.hasFetched && Boolean(applicant.firstName)
                          }
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(applicant.id, "firstName", value);
                            }
                          }}
                          maxLength={100}
                          placeholder="First name"
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        {!applicant.firstName &&
                          applicant.formErrors?.firstName && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.firstName}
                            </p>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Middle Name</Label> */}
                        <InputFieldset
                          label="Middle Name"
                          value={applicant.middleName}
                          readOnly={applicant.hasFetched}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "middleName",
                                value,
                              );
                            }
                          }}
                          placeholder="Middle name"
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        {applicant.formErrors?.middleName && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.middleName}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Last Name <span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Last Name"
                          required
                          value={applicant.lastName}
                          readOnly={applicant.hasFetched}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(applicant.id, "lastName", value);
                            }
                          }}
                          placeholder="Last name"
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        {!applicant.lastName &&
                          applicant.formErrors?.lastName && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.lastName}
                            </p>
                          )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <InputFieldset
                          label="Date of Birth"
                          required={applicant.isNonItsUser}
                          type="date"
                          value={applicant.dob}
                          max={new Date().toISOString().split("T")[0]}
                          readOnly={applicant.hasFetched}
                          onChange={(e) =>
                            handleDateOfBirthChange(
                              applicant.id,
                              e.target.value,
                            )
                          }
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        {!applicant.dob && applicant.formErrors?.dob && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.dob}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Age</Label> */}
                        <InputFieldset
                          label="Age"
                          type="number"
                          value={applicant.age}
                          // readOnly={applicant.hasFetched}
                          readOnly
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <SelectFieldset
                          label="Gender"
                          required={applicant.isNonItsUser}
                        >
                          <Select
                            value={applicant.gender}
                            disabled={applicant.hasFetched}
                            onValueChange={(value) =>
                              updateApplicant(applicant.id, "gender", value)
                            }
                          >
                            <SelectTriggerUI
                              className={`bg-input-background border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                            >
                              <SelectValue placeholder="Select" />
                            </SelectTriggerUI>
                            <SelectContent>
                              {genders.map((g) => (
                                <SelectItem key={g} value={g}>
                                  {g}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                        {!applicant.gender && applicant.formErrors?.gender && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.gender}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Birth Place</Label> */}
                        <InputFieldset
                          label="Birth Place"
                          value={applicant.birthPlace}
                          readOnly={applicant.hasFetched}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "birthPlace",
                                value,
                              );
                            }
                          }}
                          placeholder="Birth place"
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        {applicant.formErrors?.birthPlace && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.birthPlace}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Nationality, Applicant Type, Category & Guardian ITS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Nationality<span className="text-red-500">*</span></Label> */}
                        {/* <InputFieldset
                          label="Nationality"
                          required
                          value={applicant.nationality}
                          readOnly={applicant.hasFetched}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value.length <= 100 && /^[A-Za-z ]*$/.test(value)) {
                              updateApplicant(applicant.id, 'nationality', value);
                            }
                          }}
                          placeholder="Nationality"
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        /> */}

                        <SelectFieldset label="Nationality" required>
                          <Popover
                            open={openCountryField === `${applicant.id}-basic`}
                            onOpenChange={(isOpen) =>
                              setOpenCountryField(
                                isOpen ? `${applicant.id}-basic` : null,
                              )
                            }
                          >
                            <PopoverTrigger asChild>
                              <PopoverTriggerUI
                                variant="outline"
                                role="combobox"
                                aria-expanded={
                                  openCountryField === `${applicant.id}-basic`
                                }
                                disabled={applicant.hasFetched}
                                className={cn(
                                  "bg-input-background h-11 w-full justify-between bg-white border-gray-300",
                                  applicant.hasFetched &&
                                    "cursor-not-allowed bg-gray-100",
                                )}
                              >
                                {applicant.nationality
                                  ? countries.find(
                                      (c) =>
                                        String(c.nationality) ===
                                        applicant.nationality,
                                    )?.nationality || applicant.nationality
                                  : "Select Nationality..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </PopoverTriggerUI>
                            </PopoverTrigger>
                            <PopoverContent className="min-w-[300px] w-full p-0">
                              <Command>
                                <CommandInput
                                  value={search}
                                  onValueChange={setSearch}
                                  placeholder="Search country..."
                                  className="h-9 focus:outline-none focus:ring-0 focus:border-transparent"
                                />
                                <CommandEmpty>No country found.</CommandEmpty>
                                <CommandGroup className="max-h-[300px] overflow-y-auto">
                                  {filteredCountries.map((country) => (
                                    <CommandItem
                                      key={country.countryId}
                                      value={country.nationality}
                                      // value={country.countryName || country.nationality}
                                      onSelect={() => {
                                        updateApplicant(
                                          applicant.id,
                                          "nationality",
                                          String(country.nationality),
                                        );
                                        setOpenCountryField(null);
                                      }}
                                    >
                                      {country.nationality}
                                      <Check
                                        className={cn(
                                          "ml-auto h-4 w-4",
                                          applicant.nationality ===
                                            String(country.nationality)
                                            ? "opacity-100"
                                            : "opacity-0",
                                        )}
                                      />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </SelectFieldset>

                        {!applicant.nationality &&
                          applicant.formErrors?.nationality && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.nationality}
                            </p>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Applicant Type <span className="text-red-500">*</span></Label> */}
                        <SelectFieldset label="Applicant Type" required>
                          <Select
                            value={applicant.applicantType}
                            disabled={
                              applicant.hasFetched || Boolean(applicant.dob)
                            }
                            onValueChange={(
                              value: "adult" | "infant" | "senior" | "child",
                            ) =>
                              updateApplicant(
                                applicant.id,
                                "applicantType",
                                value,
                              )
                            }
                          >
                            <SelectTriggerUI
                              className={`bg-input-background border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                            >
                              <SelectValue placeholder="Select type" />
                            </SelectTriggerUI>
                            <SelectContent>
                              <SelectItem value="adult">Adult</SelectItem>
                              <SelectItem value="infant">Infant</SelectItem>
                              <SelectItem value="senior">Senior</SelectItem>
                              <SelectItem value="child">Child</SelectItem>
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                        {!applicant.applicantType &&
                          applicant.formErrors?.applicantType && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.applicantType}
                            </p>
                          )}
                      </div>
                      {/* <div className="space-y-2">
                        <Label className="text-sm text-gray-700">Category</Label>
                        <Select
                          value={applicant.category}
                          disabled={true}
                          onValueChange={(value) => updateApplicant(applicant.id, 'category', value)}
                        >
                          <SelectTrigger className="bg-input-background border-gray-300 rounded-lg">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {applicant.formErrors?.category && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.category}
                          </p>
                        )}
                      </div> */}
                      {!applicant.isNonItsUser && (
                        <div className="space-y-2">
                          {/* <Label className="text-sm text-gray-700">Category</Label> */}
                          <InputFieldset
                            label="Category"
                            // required
                            value={applicant.category}
                            readOnly={applicant.hasFetched}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (
                                value.length <= 100 &&
                                /^[A-Za-z ]*$/.test(value)
                              ) {
                                updateApplicant(
                                  applicant.id,
                                  "category",
                                  value,
                                );
                              }
                            }}
                            placeholder="Category"
                            className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                          />
                          {applicant.formErrors?.category && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.category}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Guardian ITS</Label> */}
                        <InputFieldset
                          label="Guardian ITS"
                          value={applicant.guardianIts}
                          onChange={(e) =>
                            updateApplicant(
                              applicant.id,
                              "guardianIts",
                              e.target.value,
                            )
                          }
                          placeholder="Enter Guardian ITS"
                          maxLength={8}
                          className="border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* HOF ITS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">HOF ITS</Label> */}
                        <InputFieldset
                          label="HOF ITS"
                          value={applicant.hofIts}
                          readOnly={applicant.hasFetched}
                          onChange={(e) =>
                            updateApplicant(
                              applicant.id,
                              "hofIts",
                              e.target.value,
                            )
                          }
                          placeholder="Enter HOF ITS"
                          maxLength={8}
                          className={`border-gray-300 rounded-lg ${applicant.hasFetched ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Passport Information Section */}
                  <div className="space-y-4">
                    <h3
                      className="text-sm flex items-center gap-2"
                      style={{ color: "#5B9BD5" }}
                    >
                      <Globe className="w-4 h-4" />
                      Passport Information (Upload the passport copy for
                      pre-filled details)
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport Number<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Passport Number"
                          required
                          value={applicant.passportNo}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();

                            if (
                              value.length <= 12 &&
                              /^[A-Za-z0-9]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "passportNo",
                                value,
                              );
                            }
                          }}
                          placeholder="Passport number"
                          className="border-gray-300 rounded-lg"
                        />
                        {applicant.formErrors?.passportNo && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportNo}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport Type<span className="text-red-500">*</span></Label> */}
                        <SelectFieldset label="Passport Type" required>
                          <Select
                            value={applicant.passportType}
                            onValueChange={(value) =>
                              updateApplicant(
                                applicant.id,
                                "passportType",
                                value,
                              )
                            }
                          >
                            <SelectTriggerUI className="bg-input-background border-gray-300 rounded-lg">
                              <SelectValue placeholder="Select type" />
                            </SelectTriggerUI>
                            <SelectContent>
                              {passportTypes.map((pt) => (
                                <SelectItem key={pt} value={pt}>
                                  {pt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                        {!applicant.passportType &&
                          applicant.formErrors?.passportType && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportType}
                            </p>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Place of Issue<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Place of Issue"
                          required
                          // value={""}
                          value={applicant.passportIssuePlace}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "passportIssuePlace",
                                value,
                              );
                            }
                          }}
                          placeholder="Issue place"
                          className="border-gray-300 rounded-lg"
                        />
                        {!applicant.passportIssuePlace &&
                          applicant.formErrors?.passportIssuePlace && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportIssuePlace}
                            </p>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Profession</Label> */}
                        <InputFieldset
                          label="Profession"
                          value={applicant.profession}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "profession",
                                value,
                              );
                            }
                          }}
                          placeholder="Profession"
                          className="border-gray-300 rounded-lg"
                        />
                        {applicant.formErrors?.profession && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.profession}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport First Name<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Passport First Name"
                          required
                          value={applicant.passportFirstName}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "passportFirstName",
                                value,
                              );
                            }
                          }}
                          placeholder="As per passport"
                          className={`border-gray-300 rounded-lg ${
                            applicant.showValidationErrors &&
                            applicant.validationErrors?.find(
                              (v) =>
                                v.field === "firstName" &&
                                !v.isValid &&
                                !v.remark,
                            )
                              ? "border-red-500 border-2"
                              : ""
                          }`}
                        />
                        {!applicant.passportFirstName &&
                          applicant.formErrors?.passportFirstName && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportFirstName}
                            </p>
                          )}
                        {applicant.showValidationErrors &&
                          applicant.validationErrors?.find(
                            (v) => v.field === "firstName" && !v.isValid,
                          ) && (
                            <div className="space-y-2">
                              <p className="text-xs text-red-600">
                                ⚠ Mismatch: ITS Name "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "firstName",
                                  )?.itsValue
                                }
                                " vs Passport "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "firstName",
                                  )?.passportValue
                                }
                                "
                              </p>
                              <Input
                                value={
                                  applicant.validationErrors.find(
                                    (v) => v.field === "firstName",
                                  )?.remark || ""
                                }
                                placeholder="Add remark to accept this name..."
                                className={`text-xs ${
                                  applicant.validationErrors.find(
                                    (v) => v.field === "firstName",
                                  )?.remark
                                    ? "border-green-500 border-2"
                                    : "border-amber-400"
                                }`}
                                onChange={(e) =>
                                  updateValidationRemark(
                                    applicant.id,
                                    "firstName",
                                    e.target.value,
                                  )
                                }
                                onBlur={(e) =>
                                  handleRemarkBlur(
                                    applicant.id,
                                    "firstName",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport Middle Name</Label> */}
                        <InputFieldset
                          label="Passport Middle Name"
                          value={applicant.passportMiddleName}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "passportMiddleName",
                                value,
                              );
                            }
                          }}
                          placeholder="As per passport"
                          className={`border-gray-300 rounded-lg ${
                            applicant.showValidationErrors &&
                            applicant.validationErrors?.find(
                              (v) =>
                                v.field === "middleName" &&
                                !v.isValid &&
                                !v.remark,
                            )
                              ? "border-red-500 border-2"
                              : ""
                          }`}
                        />
                        {applicant.showValidationErrors &&
                          applicant.validationErrors?.find(
                            (v) => v.field === "middleName" && !v.isValid,
                          ) && (
                            <div className="space-y-2">
                              <p className="text-xs text-red-600">
                                ⚠ Mismatch: ITS Name "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "middleName",
                                  )?.itsValue
                                }
                                " vs Passport "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "middleName",
                                  )?.passportValue
                                }
                                "
                              </p>
                              <Input
                                value={
                                  applicant.validationErrors.find(
                                    (v) => v.field === "middleName",
                                  )?.remark || ""
                                }
                                placeholder="Add remark to accept this name..."
                                className={`text-xs ${
                                  applicant.validationErrors.find(
                                    (v) => v.field === "middleName",
                                  )?.remark
                                    ? "border-green-500 border-2"
                                    : "border-amber-400"
                                }`}
                                onChange={(e) =>
                                  updateValidationRemark(
                                    applicant.id,
                                    "middleName",
                                    e.target.value,
                                  )
                                }
                                onBlur={(e) =>
                                  handleRemarkBlur(
                                    applicant.id,
                                    "middleName",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                        {applicant.formErrors?.passportMiddleName && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportMiddleName}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport Last Name<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Passport Last Name"
                          required
                          value={applicant.passportLastName}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (
                              value.length <= 100 &&
                              /^[A-Za-z ]*$/.test(value)
                            ) {
                              updateApplicant(
                                applicant.id,
                                "passportLastName",
                                value,
                              );
                            }
                          }}
                          placeholder="As per passport"
                          className={`border-gray-300 rounded-lg ${
                            applicant.showValidationErrors &&
                            applicant.validationErrors?.find(
                              (v) =>
                                v.field === "lastName" &&
                                !v.isValid &&
                                !v.remark,
                            )
                              ? "border-red-500 border-2"
                              : ""
                          }`}
                        />
                        {!applicant.passportLastName &&
                          applicant.formErrors?.passportLastName && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportLastName}
                            </p>
                          )}
                        {applicant.showValidationErrors &&
                          applicant.validationErrors?.find(
                            (v) => v.field === "lastName" && !v.isValid,
                          ) && (
                            <div className="space-y-2">
                              <p className="text-xs text-red-600">
                                ⚠ Mismatch: ITS Name "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "lastName",
                                  )?.itsValue
                                }
                                " vs Passport "
                                {
                                  applicant.validationErrors.find(
                                    (v) => v.field === "lastName",
                                  )?.passportValue
                                }
                                "
                              </p>
                              <Input
                                value={
                                  applicant.validationErrors.find(
                                    (v) => v.field === "lastName",
                                  )?.remark || ""
                                }
                                placeholder="Add remark to accept this name..."
                                className={`text-xs ${
                                  applicant.validationErrors.find(
                                    (v) => v.field === "lastName",
                                  )?.remark
                                    ? "border-green-500 border-2"
                                    : "border-amber-400"
                                }`}
                                onChange={(e) =>
                                  updateValidationRemark(
                                    applicant.id,
                                    "lastName",
                                    e.target.value,
                                  )
                                }
                                onBlur={(e) =>
                                  handleRemarkBlur(
                                    applicant.id,
                                    "lastName",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Passport Arabic Name</Label> */}
                        <InputFieldset
                          label="Passport Arabic Name"
                          value={applicant.passportArabicName}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value.length <= 300) {
                              updateApplicant(
                                applicant.id,
                                "passportArabicName",
                                value,
                              );
                            }
                          }}
                          placeholder="Arabic name"
                          className="border-gray-300 rounded-lg"
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Issue Date<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Passport Issue Date"
                          required
                          type="date"
                          value={applicant.passportIssueDate}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) =>
                            updateApplicant(
                              applicant.id,
                              "passportIssueDate",
                              e.target.value,
                            )
                          }
                          className="border-gray-300 rounded-lg"
                        />
                        {!applicant.passportIssueDate &&
                          applicant.formErrors?.passportIssueDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportIssueDate}
                            </p>
                          )}
                        {applicant.passportIssueDate &&
                          applicant.formErrors?.passportIssueDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportIssueDate}
                            </p>
                          )}
                      </div>
                      <div className="space-y-2">
                        {/* <Label className="text-sm text-gray-700">Expiry Date<span className="text-red-500">*</span></Label> */}
                        <InputFieldset
                          label="Passport Expiry Date"
                          required
                          type="date"
                          value={applicant.passportExpiryDate}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(e) =>
                            updateApplicant(
                              applicant.id,
                              "passportExpiryDate",
                              e.target.value,
                            )
                          }
                          className="border-gray-300 rounded-lg"
                        />
                        {!applicant.passportExpiryDate &&
                          applicant.formErrors?.passportExpiryDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportExpiryDate}
                            </p>
                          )}
                        {applicant.passportExpiryDate &&
                          applicant.formErrors?.passportExpiryDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {applicant.formErrors.passportExpiryDate}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Passport DOB, Gender, Nationality for validation */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      {/* <Label className="text-sm text-gray-700">Passport Date of Birth<span className="text-red-500">*</span></Label> */}
                      <InputFieldset
                        label="DOB as per Passport"
                        required
                        type="date"
                        value={applicant.passportDob}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) =>
                          updateApplicant(
                            applicant.id,
                            "passportDob",
                            e.target.value,
                          )
                        }
                        placeholder="As per passport"
                        className={`border-gray-300 rounded-lg ${
                          applicant.showValidationErrors &&
                          applicant.validationErrors?.find(
                            (v) => v.field === "dob" && !v.isValid && !v.remark,
                          )
                            ? "border-red-500 border-2"
                            : ""
                        }`}
                      />
                      {!applicant.passportDob &&
                        applicant.formErrors?.passportDob && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportDob}
                          </p>
                        )}
                      {applicant.passportDob &&
                        applicant.formErrors?.passportDob && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportDob}
                          </p>
                        )}
                      {applicant.showValidationErrors &&
                        applicant.validationErrors?.find(
                          (v) => v.field === "dob" && !v.isValid,
                        ) && (
                          <div className="space-y-2">
                            <p className="text-xs text-red-600">
                              ⚠ Mismatch: ITS DOB "
                              {
                                applicant.validationErrors.find(
                                  (v) => v.field === "dob",
                                )?.itsValue
                              }
                              " vs Passport "
                              {
                                applicant.validationErrors.find(
                                  (v) => v.field === "dob",
                                )?.passportValue
                              }
                              "
                            </p>
                            <Input
                              value={
                                applicant.validationErrors.find(
                                  (v) => v.field === "dob",
                                )?.remark || ""
                              }
                              placeholder="Add remark to accept this DOB..."
                              className={`text-xs ${
                                applicant.validationErrors.find(
                                  (v) => v.field === "dob",
                                )?.remark
                                  ? "border-green-500 border-2"
                                  : "border-amber-400"
                              }`}
                              onChange={(e) =>
                                updateValidationRemark(
                                  applicant.id,
                                  "dob",
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleRemarkBlur(
                                  applicant.id,
                                  "dob",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        )}
                    </div>

                    <div className="space-y-2">
                      {/* <Label className="text-sm text-gray-700">Passport Gender</Label> */}
                      <SelectFieldset label="Gender as per Passport" required>
                        <Select
                          value={applicant.passportGender}
                          onValueChange={(value) =>
                            updateApplicant(
                              applicant.id,
                              "passportGender",
                              value,
                            )
                          }
                        >
                          <SelectTriggerUI
                            className={`bg-input-background border-gray-300 rounded-lg ${
                              applicant.showValidationErrors &&
                              applicant.validationErrors?.find(
                                (v) =>
                                  v.field === "gender" &&
                                  !v.isValid &&
                                  !v.remark,
                              )
                                ? "border-red-500 border-2"
                                : ""
                            }`}
                          >
                            <SelectValue placeholder="Select Gender" />
                          </SelectTriggerUI>
                          <SelectContent>
                            {genders.map((g) => (
                              <SelectItem key={g} value={g}>
                                {g}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </SelectFieldset>
                      {!applicant.passportGender &&
                        applicant.formErrors?.passportGender && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportGender}
                          </p>
                        )}
                      {/* {applicant.formErrors?.passportGender && (
                        <p className="text-red-500 text-xs mt-1">
                          {applicant.formErrors.passportGender}
                        </p>
                      )} */}
                      {applicant.showValidationErrors &&
                        applicant.validationErrors?.find(
                          (v) => v.field === "gender" && !v.isValid,
                        ) && (
                          <div className="space-y-2">
                            <p className="text-xs text-red-600">
                              ⚠ Mismatch: ITS Gender "
                              {
                                applicant.validationErrors.find(
                                  (v) => v.field === "gender",
                                )?.itsValue
                              }
                              " vs Passport "
                              {
                                applicant.validationErrors.find(
                                  (v) => v.field === "gender",
                                )?.passportValue
                              }
                              "
                            </p>
                            <Input
                              value={
                                applicant.validationErrors.find(
                                  (v) => v.field === "gender",
                                )?.remark || ""
                              }
                              placeholder="Add remark to accept this gender..."
                              className={`text-xs ${
                                applicant.validationErrors.find(
                                  (v) => v.field === "gender",
                                )?.remark
                                  ? "border-green-500 border-2"
                                  : "border-amber-400"
                              }`}
                              onChange={(e) =>
                                updateValidationRemark(
                                  applicant.id,
                                  "gender",
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleRemarkBlur(
                                  applicant.id,
                                  "gender",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        )}
                    </div>

                    <div className="space-y-2">
                      <SelectFieldset
                        label="Nationality as per Passport"
                        required
                      >
                        <Popover
                          open={openCountryField === `${applicant.id}-passport`}
                          onOpenChange={(isOpen) =>
                            setOpenCountryField(
                              isOpen ? `${applicant.id}-passport` : null,
                            )
                          }
                        >
                          <PopoverTrigger asChild>
                            <PopoverTriggerUI
                              variant="outline"
                              role="combobox"
                              aria-expanded={
                                openCountryField === `${applicant.id}-passport`
                              }
                              className="bg-input-background h-11 w-full justify-between bg-white border-gray-300"
                            >
                              {applicant.passportNationality
                                ? countries.find(
                                    (c) =>
                                      String(c.nationality) ===
                                      applicant.passportNationality,
                                  )?.nationality
                                : "Select Nationality..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </PopoverTriggerUI>
                          </PopoverTrigger>
                          <PopoverContent className="min-w-[300px] w-full p-0">
                            <Command>
                              <CommandInput
                                value={search}
                                onValueChange={setSearch}
                                placeholder="Search country..."
                                className="h-9 focus:outline-none focus:ring-0 focus:border-transparent"
                              />
                              <CommandEmpty>No country found.</CommandEmpty>
                              <CommandGroup className="max-h-[300px] overflow-y-auto">
                                <CommandItem
                                  value="ALL_NATIONALITIES"
                                  onSelect={() => {
                                    updateApplicant(
                                      applicant.id,
                                      "passportNationality",
                                      "",
                                    );
                                    setOpenCountryField(null);
                                  }}
                                >
                                  All Nationalities
                                  <Check
                                    className={cn(
                                      "ml-auto h-4 w-4",
                                      applicant.passportNationality === ""
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                </CommandItem>

                                {filteredCountries.map((country) => (
                                  <CommandItem
                                    key={country.countryId}
                                    value={country.nationality}
                                    // value={country.countryName || country.nationality}
                                    onSelect={() => {
                                      updateApplicant(
                                        applicant.id,
                                        "passportNationality",
                                        String(country.nationality),
                                      );
                                      setOpenCountryField(null);
                                    }}
                                  >
                                    {country.nationality}
                                    <Check
                                      className={cn(
                                        "ml-auto h-4 w-4",
                                        applicant.passportNationality ===
                                          String(country.nationality)
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </SelectFieldset>

                      {!applicant.passportNationality &&
                        applicant.formErrors?.passportNationality && (
                          <p className="text-red-500 text-xs mt-1">
                            {applicant.formErrors.passportNationality}
                          </p>
                        )}
                    </div>
                  </div>

                  <Separator />

                  {/* Family Information Section */}
                  {/* Family Accordion start */}
                  <div
                    className={`bg-white border border-default shadow-xs ${!applicant.isFamilyOpen ? "rounded-md" : ""}`}
                  >
                    <h2>
                      <button
                        type="button"
                        onClick={() => toggleFamilySection(applicant.id)}
                        className={`flex items-center justify-between w-full p-4 font-medium text-body rounded-base hover:text-heading hover:bg-neutral-secondary-medium ${
                          applicant.isFamilyOpen
                            ? "rounded-b-none shadow-none"
                            : ""
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <h3
                              className="text-sm flex items-center gap-2"
                              style={{ color: "#5B9BD5" }}
                            >
                              <User className="w-4 h-4" />
                              Family Information
                            </h3>
                          </div>
                        </div>
                        {applicant.isFamilyOpen ? (
                          <ChevronUp className="w-5 h-5 text-blue-700" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-blue-700" />
                        )}
                      </button>
                    </h2>

                    {applicant.isFamilyOpen && (
                      <div className="border-default p-2 md:p-2">
                        <div className="space-y-4 p-4">
                          {/* Father Information */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Father's ITS No.</Label> */}
                              <InputFieldset
                                label="Father's ITS No."
                                value={applicant.fatherItsNo}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 8 &&
                                    /^\d*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "fatherItsNo",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="8-digit ITS number"
                                maxLength={8}
                                className="border-gray-300 rounded-lg"
                              />
                              {applicant.formErrors?.fatherItsNo && (
                                <p className="text-red-500 text-xs mt-1">
                                  {applicant.formErrors.fatherItsNo}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Father's Name</Label> */}
                              <InputFieldset
                                label="Father's Name"
                                value={applicant.fatherName}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 200 &&
                                    /^[A-Za-z ]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "fatherName",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Father's name"
                                className="border-gray-300 rounded-lg"
                              />
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Father's Name (Arabic)</Label> */}
                              <InputFieldset
                                label="Father's Name (Arabic)"
                                value={applicant.fatherNameArabic}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 300 &&
                                    /^[\u0600-\u06FF\s]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "fatherNameArabic",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Father's name in Arabic"
                                className="border-gray-300 rounded-lg"
                                dir="rtl"
                              />
                            </div>
                          </div>

                          {/* Grandfather Information */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Grandfather's ITS No.</Label> */}
                              <InputFieldset
                                label="Grandfather's ITS No."
                                value={applicant.grandfatherItsNo}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 8 &&
                                    /^\d*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "grandfatherItsNo",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="8-digit ITS number"
                                maxLength={8}
                                className="border-gray-300 rounded-lg"
                              />
                              {applicant.formErrors?.grandfatherItsNo && (
                                <p className="text-red-500 text-xs mt-1">
                                  {applicant.formErrors.grandfatherItsNo}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Grandfather's Name</Label> */}
                              <InputFieldset
                                label="Grandfather's Name"
                                value={applicant.grandfatherName}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 200 &&
                                    /^[A-Za-z ]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "grandfatherName",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Grandfather's name"
                                className="border-gray-300 rounded-lg"
                              />
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Grandfather's Name (Arabic)</Label> */}
                              <InputFieldset
                                label="Grandfather's Name (Arabic)"
                                value={applicant.grandfatherNameArabic}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 300 &&
                                    /^[\u0600-\u06FF\s]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "grandfatherNameArabic",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Grandfather's name in Arabic"
                                className="border-gray-300 rounded-lg"
                                dir="rtl"
                              />
                            </div>
                          </div>

                          {/* Mother Information */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's ITS No.</Label> */}
                              <InputFieldset
                                label="Mother's ITS No."
                                value={applicant.motherItsNo}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 8 &&
                                    /^\d*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "motherItsNo",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="8-digit ITS number"
                                maxLength={8}
                                className="border-gray-300 rounded-lg"
                              />
                              {applicant.formErrors?.motherItsNo && (
                                <p className="text-red-500 text-xs mt-1">
                                  {applicant.formErrors.motherItsNo}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's Name</Label> */}
                              <InputFieldset
                                label="Mother's Name"
                                value={applicant.motherName}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 200 &&
                                    /^[A-Za-z ]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "motherName",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Mother's name"
                                className="border-gray-300 rounded-lg"
                              />
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's Name (Arabic)</Label> */}
                              <InputFieldset
                                label="Mother's Name (Arabic)"
                                value={applicant.motherNameArabic}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 300 &&
                                    /^[\u0600-\u06FF\s]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "motherNameArabic",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Mother's name in Arabic"
                                className="border-gray-300 rounded-lg"
                                dir="rtl"
                              />
                            </div>
                          </div>

                          {/* Mother's Father Information */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's Father ITS No.</Label> */}
                              <InputFieldset
                                label="Mother's Father ITS No."
                                value={applicant.mothersFatherItsNo}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 8 &&
                                    /^\d*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "mothersFatherItsNo",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="8-digit ITS number"
                                maxLength={8}
                                className="border-gray-300 rounded-lg"
                              />
                              {applicant.formErrors?.mothersFatherItsNo && (
                                <p className="text-red-500 text-xs mt-1">
                                  {applicant.formErrors.mothersFatherItsNo}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's Father Name</Label> */}
                              <InputFieldset
                                label="Mother's Father Name"
                                value={applicant.mothersFatherName}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 200 &&
                                    /^[A-Za-z ]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "mothersFatherName",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Mother's father name"
                                className="border-gray-300 rounded-lg"
                              />
                            </div>
                            <div className="space-y-2">
                              {/* <Label className="text-sm text-gray-700">Mother's Father Name (Arabic)</Label> */}
                              <InputFieldset
                                label="Mother's Father Name (Arabic)"
                                value={applicant.mothersFatherNameArabic}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (
                                    value.length <= 300 &&
                                    /^[\u0600-\u06FF\s]*$/.test(value)
                                  ) {
                                    updateApplicant(
                                      applicant.id,
                                      "mothersFatherNameArabic",
                                      value,
                                    );
                                  }
                                }}
                                placeholder="Mother's father name in Arabic"
                                className="border-gray-300 rounded-lg"
                                dir="rtl"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* accordionstart */}

                  <Separator />

                  {/* Documents Upload */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <h3 className="text-base flex items-center gap-2 text-gray-900 mb-1">
                            <FileText
                              className="w-5 h-5"
                              style={{ color: "#5B9BD5" }}
                            />
                            Upload Documents{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <p className="text-xs md:text-sm text-gray-600">
                            Required documents submitted by the applicant
                          </p>
                          {/* TEST OCR CODE */}
                          {/* <div>
                            <input type="file" accept="image/png, image/jpeg, application/pdf" onChange={handleFile} />
                          </div> */}
                          {/* TEST OCR CODE END */}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="border-red-200 text-red-700 bg-red-50 text-xs"
                          >
                            {/* {applicant.documents.filter(d => d.mandatory).length} Mandatory */}
                            {
                              applicant.documents.filter((d) =>
                                d.screenNames.some((s) =>
                                  allowedScreens.includes(s),
                                ),
                              ).length
                            }{" "}
                            Mandatory
                          </Badge>
                          <Badge
                            variant="outline"
                            className="border-gray-200 text-gray-600 text-xs"
                          >
                            {
                              applicant.documents.filter((d) => !d.mandatory)
                                .length
                            }{" "}
                            Optional
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Info Banner */}
                    {applicant.formErrors?.documents && (
                      <Alert
                        variant="destructive"
                        className="mb-6 border-destructive/50 bg-destructive/10 border-red-400"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription className="text-destructive text-xs">
                          <span className="text-red-400">
                            {applicant.formErrors.documents}
                          </span>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-3">
                      {/* {applicant.documents.filter(doc => doc.screenName?.some(screen => allowedScreens.includes(screen))).map((doc, index) => ( */}
                      {applicant.documents.map((doc, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-3 md:p-4 bg-white hover:shadow-sm transition-shadow"
                        >
                          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                            {/* Number & Document Name */}
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-700">
                                {index + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-gray-900">
                                    {doc.documentType}
                                    {doc.mandatory && (
                                      <span className="text-red-500">*</span>
                                    )}
                                  </p>
                                  {!doc.mandatory && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs text-gray-500 border-gray-300"
                                    >
                                      Optional
                                    </Badge>
                                  )}
                                </div>
                                {doc.acceptedFormatsList &&
                                  doc.acceptedFormatsList.length > 0 && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {/* Formats: {doc.acceptedFormatsList.join(', ')} • Max: {doc.maxSize}MB */}
                                      Formats:{" "}
                                      {doc.acceptedFormatsList.join(", ")} •
                                      Max: {Number(doc.maxSize) / 1024}MB
                                    </p>
                                  )}
                              </div>
                            </div>

                            {/* Status & Actions */}
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3 mt-2 md:mt-0">
                              {doc.fileName ? (
                                <>
                                  {/* Uploaded Status */}
                                  <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                                    <span className="text-xs md:text-sm">
                                      Document uploaded
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 w-full md:w-auto">
                                    {/* View Button */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 md:h-9 px-3 md:px-4 text-xs md:text-sm border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/10 rounded-full flex-1 md:flex-none"
                                      onClick={() =>
                                        handleViewDocument(doc.file)
                                      }
                                    >
                                      <Eye className="w-3 h-3 md:w-4 md:h-4 md:mr-1" />
                                      <span className="hidden md:inline">
                                        View
                                      </span>
                                    </Button>

                                    {/* Delete Button */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 md:h-9 w-8 md:w-9 p-0 text-red-600 hover:bg-red-50 rounded-lg"
                                      onClick={() =>
                                        handleRemoveDocument(
                                          applicant.id,
                                          doc.documentType,
                                        )
                                      }
                                    >
                                      <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                                    </Button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Not Uploaded Status */}
                                  <div className="flex items-center gap-2 text-gray-400">
                                    <Clock className="w-4 h-4 md:w-5 md:h-5" />
                                    <span className="text-xs md:text-sm">
                                      Pending upload
                                    </span>
                                  </div>

                                  {/* Upload Button */}
                                  <label
                                    htmlFor={`doc-${applicant.id}-${index}`}
                                    className="w-full md:w-auto"
                                  >
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 md:h-9 px-3 md:px-4 text-xs md:text-sm border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/10 rounded-full w-full md:w-auto"
                                      onClick={() =>
                                        document
                                          .getElementById(
                                            `doc-${applicant.id}-${index}`,
                                          )
                                          ?.click()
                                      }
                                    >
                                      <Upload className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                                      Upload
                                    </Button>
                                  </label>
                                  <Input
                                    type="file"
                                    accept={
                                      doc.acceptedFormatsList
                                        ? doc.acceptedFormatsList
                                            .map((format) => {
                                              const formatMap: Record<
                                                string,
                                                string
                                              > = {
                                                PDF: "application/pdf",
                                                JPG: "image/jpeg",
                                                PNG: "image/png",
                                                JPEG: "image/jpeg",
                                              };
                                              return (
                                                formatMap[
                                                  format.toUpperCase()
                                                ] || ""
                                              );
                                            })
                                            .filter(Boolean)
                                            .join(", ")
                                        : "image/jpeg, image/jpg, application/pdf"
                                    }
                                    onChange={(e) =>
                                      handleDocumentUpload(
                                        applicant.id,
                                        doc.documentType,
                                        e,
                                      )
                                    }
                                    className="hidden"
                                    id={`doc-${applicant.id}-${index}`}
                                  />
                                </>
                              )}
                            </div>
                          </div>

                          {/* File Info */}
                          {doc.fileName && (
                            <div className="ml-12 mt-2">
                              <p className="text-xs text-gray-500">
                                {doc.fileName}
                                {doc.uploadedDate && (
                                  <span className="ml-2">
                                    • Uploaded on{" "}
                                    {new Date(
                                      doc.uploadedDate,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </span>
                                )}
                              </p>
                            </div>
                          )}
                          {/* Per-document validation error */}
                          {doc.mandatory &&
                            !doc.fileName &&
                            applicant.formErrors?.documents && (
                              <div className="mt-2 ml-10">
                                <p className="text-xs text-red-500">
                                  {doc.documentType} is required.
                                </p>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                    {/* {ocrError && (
                      <p className="text-red-500 text-xs mt-2">
                        {ocrError}
                      </p>
                    )} */}
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Paperclip
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: "#5B9BD5" }}
                      />
                      <p>
                        Allowed formats and maximum size are shown for each
                        document.
                      </p>
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="space-y-2">
                    <SelectFieldset label="Remarks">
                      <TextareaUI
                        value={applicant.remarks}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value.length <= 1000) {
                            updateApplicant(applicant.id, "remarks", value);
                          }
                        }}
                        placeholder="Add any additional notes or remarks..."
                        className={`min-h-[60px] ${applicant.formErrors?.remarks ? "border-red-500" : ""}`}
                      />
                    </SelectFieldset>
                    {applicant.remarks && applicant.formErrors?.remarks && (
                      <p className="text-red-500 text-xs mt-1">
                        {applicant.formErrors.remarks}
                      </p>
                    )}
                  </div>

                  <Separator />

                  {/* Delete Button */}
                    <div className="flex items-center justify-end pt-2">
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteApplicant(applicant.id)}
                        className="text-red-600 hover:bg-red-50 border-red-200 rounded-lg w-full md:w-auto"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Applicant
                      </Button>
                    </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Add New Applicant Button */}
      <Button
        onClick={handleAddNewApplicant}
        className="w-full h-12 text-white rounded-xl mb-6"
        style={{ backgroundColor: "#5B9BD5" }}
      >
        <Plus className="w-5 h-5 mr-2" />
        Add New Applicant
      </Button>

      {/* Final Action Buttons */}
      <div className="flex flex-col md:flex-row justify-end gap-3 mb-4 md:gap-4">
        <Button
          disabled={isSubmitting}
          onClick={() => {
            setPendingAction("submit");
            setConfirmDialogOpen(true);
          }}
          className="text-white rounded-xl px-6 w-full md:w-auto"
          style={{ backgroundColor: "#5B9BD5" }}
        >
          {isSubmitting ? "Processing..." : "Add Applicant"}
        </Button>
      </div>

      <ConfirmDialog
        show={false}
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        title={
          pendingAction === "draft"
            ? "Confirm Save as Draft"
            : "Confirm Visa Applicant"
        }
        description={
          pendingAction === "draft"
            ? "Are you sure you want to save this application as draft?"
            : "Are you sure you want to submit this application for visa processing?"
        }
        confirmText={isSubmitting ? "Loading..." : "Yes, Confirm"}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setPendingAction(null);
        }}
      />
    </div>
  );
}
